import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../analytics';
import { MOTIONPACE_MARKETING_ENABLED } from '../lib/motionPaceMarketing';
import './GuidesIndexPage.css';

function buildJsonLd() {
  const url = 'https://tweediastudio.com/guides';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${url}#collection`,
        name: 'Timelapse Guides & Free Tools',
        description:
          'Free guides and tools to help you shoot and edit long-term timelapses — construction, renovation, garden, and workshop projects.',
        url,
      },
    ],
  };
}

function GuidesIndexPage({ user, signIn }) {
  usePageMeta({
    title: 'Timelapse Guides & Free Tools | Tweedia Studio',
    description:
      'Free guides and tools for shooting and editing long-term timelapses — cameras, intervals, storage math, and the edit pipeline.',
    canonicalPath: '/guides',
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trackNav = (target) => () => {
    trackEvent('nav_click', { target });
  };

  return (
    <div className="guides-index-page">
      <Header user={user} signIn={signIn} />
      <main className="guides-index-content" data-testid="guides-index-content">
        <section className="guides-index-hero">
          <p className="guides-index-eyebrow">Guides & Tools</p>
          <h1>Guides & Free Tools</h1>
          <p>
            Everything you need to plan, shoot, and edit a long-term timelapse — from picking
            a camera to nailing the interval to turning the raw capture into a video people
            actually want to watch.
          </p>
        </section>

        <div className="guides-index-grid">
          {/*
            The "What is adaptive pacing?" pillar (#546). Gated on the same
            build-time switch as /motionpace, because the index is the only
            internal surface that links it — a card here while the feature is
            dark would be the one place production leaked it. Rendered as a bare
            `&&` rather than a mapped list so the production markup is
            byte-identical to what it was before, exactly as Header does it.
            First in the grid: it is the term the rest of the product is named
            for, so it leads once it is lit.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Link
              to="/guides/what-is-adaptive-pacing"
              className="guides-index-card"
              data-testid="guides-card-adaptive-pacing"
              onClick={trackNav('guide_what_is_adaptive_pacing')}
            >
              <div className="guides-index-card-badges">
                <span className="guides-index-badge">Guide</span>
                <span className="guides-index-meta">6 min read</span>
              </div>
              <h2>What Is Adaptive Pacing?</h2>
              <p>
                The plain-English definition of a timelapse whose speed follows the action —
                and how it differs from a constant-rate timelapse, a hyperlapse, and
                hand-placed speed ramps.
              </p>
              <span className="guides-index-card-cta">Read the guide →</span>
            </Link>
          )}

          {/*
            The intent-capture cluster (#547) — the three guides that answer the
            queries people actually search and link the pillar above. Gated on
            the same switch and for the same reason: the index is the only
            internal surface that links them, so a card here while MotionPace is
            dark would be the one place production leaked it. They sit directly
            under the pillar because they are its cluster; the ungated guides
            below keep their original order so the dark build's markup is
            unchanged.
          */}
          {MOTIONPACE_MARKETING_ENABLED && (
            <Link
              to="/guides/variable-speed-timelapse"
              className="guides-index-card"
              data-testid="guides-card-variable-speed"
              onClick={trackNav('guide_variable_speed_timelapse')}
            >
              <div className="guides-index-card-badges">
                <span className="guides-index-badge">Guide</span>
                <span className="guides-index-meta">7 min read</span>
              </div>
              <h2>How to Make a Variable Speed Timelapse</h2>
              <p>
                Three ways to make one clip change speed — in the camera, by hand in an
                editor, or automatically from the footage — and which of them still works on
                a shoot you have already filmed.
              </p>
              <span className="guides-index-card-cta">Read the guide →</span>
            </Link>
          )}

          {MOTIONPACE_MARKETING_ENABLED && (
            <Link
              to="/guides/skip-the-boring-parts"
              className="guides-index-card"
              data-testid="guides-card-skip-boring"
              onClick={trackNav('guide_skip_the_boring_parts')}
            >
              <div className="guides-index-card-badges">
                <span className="guides-index-badge">Guide</span>
                <span className="guides-index-meta">6 min read</span>
              </div>
              <h2>How to Skip the Boring Parts of a Timelapse</h2>
              <p>
                Two different answers: trim the stillness and the video gets shorter, or
                speed through it and keep the whole shoot. Which one you want depends on
                whether the passage of time is the point.
              </p>
              <span className="guides-index-card-cta">Read the guide →</span>
            </Link>
          )}

          <Link
            to="/guides/long-term-timelapse"
            className="guides-index-card"
            data-testid="guides-card-article"
            onClick={trackNav('guide_long_term_timelapse')}
          >
            <div className="guides-index-card-badges">
              <span className="guides-index-badge">Guide</span>
              <span className="guides-index-meta">12 min read</span>
            </div>
            <h2>How to Time-Lapse a Long-Term Project</h2>
            <p>
              The definitive walkthrough — cameras, power, intervals, storage, and the edit
              step that finally makes months of raw footage into a shareable video.
            </p>
            <span className="guides-index-card-cta">Read the guide →</span>
          </Link>

          <Link
            to="/guides/brinno-timelapse-flicker"
            className="guides-index-card"
            data-testid="guides-card-brinno"
            onClick={trackNav('guide_brinno_flicker')}
          >
            <div className="guides-index-card-badges">
              <span className="guides-index-badge">Guide</span>
              <span className="guides-index-meta">4 min read</span>
            </div>
            <h2>Brinno Timelapse Flicker, and How to Fix It</h2>
            <p>
              Why a months-long Brinno deployment comes back strobing, and how to smooth
              the clip you already have without installing anything.
            </p>
            <span className="guides-index-card-cta">Read the guide →</span>
          </Link>

          <Link
            to="/guides/gopro-time-lapse-photos-to-video"
            className="guides-index-card"
            data-testid="guides-card-gopro"
            onClick={trackNav('guide_gopro_photo_mode')}
          >
            <div className="guides-index-card-badges">
              <span className="guides-index-badge">Guide</span>
              <span className="guides-index-meta">4 min read</span>
            </div>
            <h2>GoPro Time Lapse Photos to Video</h2>
            <p>
              Time Lapse Photo mode leaves you a folder of JPGs and tells you to stitch
              them yourself. Here is the browser version of that job.
            </p>
            <span className="guides-index-card-cta">Read the guide →</span>
          </Link>

          <Link
            to="/guides/how-long-is-a-time-lapse"
            className="guides-index-card"
            data-testid="guides-card-howlong"
            onClick={trackNav('guide_how_long_is_a_time_lapse')}
          >
            <div className="guides-index-card-badges">
              <span className="guides-index-badge">Guide</span>
              <span className="guides-index-meta">5 min read</span>
            </div>
            <h2>How Long Is a Time Lapse?</h2>
            <p>
              The formula, conversion charts both ways, and what a 30-second clip costs
              you in recording time at every interval.
            </p>
            <span className="guides-index-card-cta">Read the guide →</span>
          </Link>

          <Link
            to="/tools/timelapse-calculator"
            className="guides-index-card"
            data-testid="guides-card-calculator"
            onClick={trackNav('tool_timelapse_calculator')}
          >
            <div className="guides-index-card-badges">
              <span className="guides-index-badge tool">Free tool</span>
            </div>
            <h2>Time Lapse Calculator</h2>
            <p>
              Works for any length, from a two-hour sunset to a six-month build, plus
              driving. Get the video length, the capture interval, or how long you need
              to record, with the frame count and storage that go with it.
            </p>
            <span className="guides-index-card-cta">Open the calculator →</span>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default GuidesIndexPage;
